import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus, Sparkles, Activity, Shield, AlertTriangle, Zap, Target, Clock, Star, Briefcase, DollarSign, Users, Globe, Award, BarChart, Heart, Calendar, Home, Baby, User, RefreshCw } from 'lucide-react';
import CosmicBackground from '../components/CosmicBackground';
import generateHealthData from '../data/healthPredictionData';
import generateCareerData from '../data/careerPredictionData';
import generateRelationshipData from '../data/relationshipPredictionData';
import generateFamilyData from '../data/familyPredictionData';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';

// All 8 Domains with comprehensive data
const domains = [
  {
    id: 1,
    name: 'Health & Longevity',
    icon: '💚',
    color: '#10b981',
    gradient: 'from-emerald-500 to-teal-600',
    currentScore: 85,
    trend: 'up',
    trendValue: '+12%',
    todayFocus: 'Cardiovascular wellness and immunity',
    weeklyInsight: 'Perfect week for starting new fitness routines',
    monthlyPrediction: 'Energy levels peak mid-month. Focus on preventive care.',
    yearlyOutlook: 'Strong vitality throughout 2025. Minor health concerns in Q3.',
    keyStrengths: ['Strong immunity', 'Good sleep patterns', 'Balanced nutrition'],
    areasToWatch: ['Stress management', 'Hydration levels'],
    recommendations: ['Morning yoga', '8 glasses water daily', 'Omega-3 supplements']
  },
  {
    id: 2,
    name: 'Career & Success',
    icon: '💼',
    color: '#f59e0b',
    gradient: 'from-amber-500 to-orange-600',
    currentScore: 92,
    trend: 'up',
    trendValue: '+18%',
    todayFocus: 'Leadership opportunities and presentations',
    weeklyInsight: 'Major breakthrough in ongoing projects expected',
    monthlyPrediction: 'Promotion likely. Network expansion accelerates.',
    yearlyOutlook: 'Career peaks in 2025. Multiple advancement opportunities.',
    keyStrengths: ['Strong work ethic', 'Leadership skills', 'Industry expertise'],
    areasToWatch: ['Work-life balance', 'Team delegation'],
    recommendations: ['Update LinkedIn', 'Pursue certifications', 'Mentor juniors']
  },
  {
    id: 3,
    name: 'Love & Romance',
    icon: '💗',
    color: '#ec4899',
    gradient: 'from-pink-500 to-rose-600',
    currentScore: 78,
    trend: 'stable',
    trendValue: '0%',
    todayFocus: 'Communication and emotional connection',
    weeklyInsight: 'Plan quality time with partner or potential matches',
    monthlyPrediction: 'Romance blossoms. Important conversations ahead.',
    yearlyOutlook: 'Deep commitment possible. Marriage indicators strong.',
    keyStrengths: ['Emotional maturity', 'Open communication', 'Romantic nature'],
    areasToWatch: ['Trust issues', 'Past baggage'],
    recommendations: ['Date nights weekly', 'Express feelings', 'Active listening']
  },
  {
    id: 4,
    name: 'Wealth & Money',
    icon: '💰',
    color: '#10b981',
    gradient: 'from-green-500 to-emerald-600',
    currentScore: 88,
    trend: 'up',
    trendValue: '+25%',
    todayFocus: 'Investment opportunities and savings',
    weeklyInsight: 'Good time for major purchases or investments',
    monthlyPrediction: 'Income streams multiply. Property gains likely.',
    yearlyOutlook: 'Wealth accumulates steadily. Financial security established.',
    keyStrengths: ['Smart investments', 'Multiple income sources', 'Good savings'],
    areasToWatch: ['Impulse spending', 'Risk management'],
    recommendations: ['Diversify portfolio', 'Emergency fund', 'Real estate research']
  },
  {
    id: 5,
    name: 'Family & Home',
    icon: '🏡',
    color: '#06b6d4',
    gradient: 'from-cyan-500 to-blue-600',
    currentScore: 95,
    trend: 'up',
    trendValue: '+8%',
    todayFocus: 'Family harmony and home improvements',
    weeklyInsight: 'Quality family time brings unexpected joy',
    monthlyPrediction: 'Home renovations favorable. Family bonds strengthen.',
    yearlyOutlook: 'Happy family life. Possible new additions to family.',
    keyStrengths: ['Strong bonds', 'Supportive environment', 'Good communication'],
    areasToWatch: ['Personal space', 'Boundaries'],
    recommendations: ['Family dinners', 'Home upgrades', 'Create traditions']
  },
  {
    id: 6,
    name: 'Learning & Skills',
    icon: '🎓',
    color: '#6366f1',
    gradient: 'from-indigo-500 to-purple-600',
    currentScore: 82,
    trend: 'up',
    trendValue: '+15%',
    todayFocus: 'Skill development and learning new technologies',
    weeklyInsight: 'Perfect week for courses and certifications',
    monthlyPrediction: 'Knowledge expansion accelerates. Degrees possible.',
    yearlyOutlook: 'Becoming an expert in your field. Teaching opportunities.',
    keyStrengths: ['Quick learner', 'Curiosity', 'Technical aptitude'],
    areasToWatch: ['Information overload', 'Practical application'],
    recommendations: ['Online courses', 'Read daily', 'Practice skills']
  },
  {
    id: 7,
    name: 'Spirit & Growth',
    icon: '🌅',
    color: '#8b5cf6',
    gradient: 'from-purple-500 to-violet-600',
    currentScore: 90,
    trend: 'up',
    trendValue: '+20%',
    todayFocus: 'Meditation and inner peace',
    weeklyInsight: 'Spiritual breakthrough imminent. Deep insights await.',
    monthlyPrediction: 'Awakening intensifies. Life purpose becomes clear.',
    yearlyOutlook: 'Profound spiritual growth. Service to others brings joy.',
    keyStrengths: ['Intuition', 'Mindfulness', 'Compassion'],
    areasToWatch: ['Grounding', 'Practical life balance'],
    recommendations: ['Daily meditation', 'Yoga practice', 'Spiritual reading']
  },
  {
    id: 8,
    name: 'Travel & Adventure',
    icon: '✈️',
    color: '#14b8a6',
    gradient: 'from-teal-500 to-cyan-600',
    currentScore: 75,
    trend: 'stable',
    trendValue: '+5%',
    todayFocus: 'Planning future journeys and exploration',
    weeklyInsight: 'Research destinations. Book tickets for next month.',
    monthlyPrediction: 'International travel opportunities arise.',
    yearlyOutlook: 'Multiple countries visited. Life-changing experiences.',
    keyStrengths: ['Adventurous spirit', 'Cultural openness', 'Flexibility'],
    areasToWatch: ['Budget planning', 'Safety precautions'],
    recommendations: ['Book flights', 'Learn languages', 'Travel insurance']
  }
];

const DashboardTrillionDollar = () => {
  const [expandedDomain, setExpandedDomain] = useState(null);
  const [selectedOrgan, setSelectedOrgan] = useState(null);
  const [expandedHealthSection, setExpandedHealthSection] = useState(null);
  const [expandedCareerSection, setExpandedCareerSection] = useState(null);
  const [expandedLoveSection, setExpandedLoveSection] = useState(null);
  const [selectedCareerPath, setSelectedCareerPath] = useState(null);
  const [realHealthData, setRealHealthData] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [domainsData, setDomainsData] = useState(domains);
  
  // Get birth data from localStorage
  const getBirthData = () => {
    try {
      const stored = localStorage.getItem('birthData');
      if (stored) {
        const parsed = JSON.parse(stored);
        const dateParts = parsed.date?.split('-') || ['1997', '05', '14'];
        const timeParts = parsed.time?.split(':') || ['12', '00'];
        return {
          name: parsed.full_name || 'User',
          year: parseInt(dateParts[0]),
          month: parseInt(dateParts[1]),
          day: parseInt(dateParts[2]),
          hour: parseInt(timeParts[0]),
          minute: parseInt(timeParts[1]),
          latitude: parsed.latitude || 28.6139,
          longitude: parsed.longitude || 77.2090,
          gender: parsed.gender?.toLowerCase() || 'unknown',
          dob: parsed.date || '1997-05-14'
        };
      }
    } catch (e) {
      console.error('Error parsing birth data:', e);
    }
    return {
      name: 'User', year: 1997, month: 5, day: 14,
      hour: 12, minute: 0, latitude: 30.32, longitude: 78.03,
      gender: 'male', dob: '1997-05-14'
    };
  };

  // Fetch REAL health data from algorithm
  const fetchRealHealthData = async () => {
    setHealthLoading(true);
    const birthData = getBirthData();
    
    try {
      const [dashboardRes, futureRes] = await Promise.all([
        axios.post(`${BACKEND_URL}/api/health/dashboard-card`, birthData),
        axios.post(`${BACKEND_URL}/api/health/future-predictions`, birthData)
      ]);
      
      const healthApiData = dashboardRes.data;
      const futureApiData = futureRes.data;
      
      // Calculate score from body systems
      const calculateScore = () => {
        if (!healthApiData.body_systems) return 85;
        const scores = healthApiData.body_systems.map(s => {
          switch(s.status) {
            case 'strong': return 95;
            case 'balanced': return 75;
            case 'sensitive': return 55;
            default: return 65;
          }
        });
        return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
      };
      
      // Get current year outlook
      const currentYearPred = futureApiData.predictions?.find(p => p.year === new Date().getFullYear());
      
      // Update health domain with REAL data
      setDomainsData(prev => prev.map(d => {
        if (d.id === 1) { // Health domain
          return {
            ...d,
            currentScore: calculateScore(),
            trend: healthApiData.overall_status === 'Excellent' ? 'up' : 
                   healthApiData.overall_status === 'Good' ? 'up' : 'stable',
            trendValue: healthApiData.overall_status === 'Excellent' ? '+15%' : '+5%',
            todayFocus: healthApiData.focus_area || 'General wellness',
            weeklyInsight: `Focus on ${healthApiData.sensitive_areas?.[0] || 'overall health'}`,
            monthlyPrediction: currentYearPred?.advice || 'Maintain healthy routines',
            yearlyOutlook: `${currentYearPred?.level || 'Good'} health year. Focus: ${currentYearPred?.focus_areas?.join(', ') || 'wellness'}`,
            keyStrengths: healthApiData.strong_areas?.slice(0, 3) || ['Good immunity'],
            areasToWatch: healthApiData.sensitive_areas?.slice(0, 2) || ['Stress management'],
            constitution: healthApiData.constitution,
            bodySystems: healthApiData.body_systems,
            futurePredictions: futureApiData.predictions
          };
        }
        return d;
      }));
      
      setRealHealthData({ dashboard: healthApiData, future: futureApiData });
      console.log('✅ Real health data loaded from algorithm!');
      
    } catch (error) {
      console.error('Error fetching health data:', error);
    } finally {
      setHealthLoading(false);
    }
  };

  // Fetch health data on mount
  useEffect(() => {
    fetchRealHealthData();
  }, []);
  
  // Get comprehensive data (legacy - for other domains)
  const birthDataForLegacy = getBirthData();
  const healthData = generateHealthData({ dob: birthDataForLegacy.dob });
  const careerData = generateCareerData({ dob: birthDataForLegacy.dob });
  const loveData = generateRelationshipData({ dob: birthDataForLegacy.dob });

  const getTrendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp className="w-4 h-4 text-green-400" />;
    if (trend === 'down') return <TrendingDown className="w-4 h-4 text-red-400" />;
    return <Minus className="w-4 h-4 text-yellow-400" />;
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-400';
    if (score >= 75) return 'text-blue-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <CosmicBackground showAstroChart={false}>
      <div className="min-h-screen pb-24">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-[#0a1628]/95 backdrop-blur-lg border-b border-white/10 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white mb-1">Life Dashboard</h1>
              <p className="text-sm text-white/60">Complete overview of all 8 life domains</p>
            </div>
            <div className="text-center flex items-center gap-2">
              <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                {Math.round(domainsData.reduce((sum, d) => sum + d.currentScore, 0) / domainsData.length)}
              </div>
              <div className="text-xs text-white/50">Overall Score</div>
              {healthLoading && <RefreshCw className="w-4 h-4 text-cyan-400 animate-spin" />}
            </div>
          </div>
        </div>

        {/* All 8 Domains - Using domainsData with REAL health data */}
        <div className="p-4 space-y-4">
          {domainsData.map((domain, index) => (
            <motion.div
              key={domain.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-gradient-to-br from-[#0a1628]/90 to-[#1a3a5a]/90 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden"
            >
              {/* Domain Header - Clickable */}
              <button
                onClick={() => setExpandedDomain(expandedDomain === domain.id ? null : domain.id)}
                className="w-full p-4 flex items-center gap-4 hover:bg-white/5 transition-all"
              >
                {/* Icon */}
                <div 
                  className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl flex-shrink-0 bg-gradient-to-br ${domain.gradient}`}
                >
                  {domain.icon}
                </div>

                {/* Main Info */}
                <div className="flex-1 text-left min-w-0">
                  <h3 className="text-white font-bold text-lg mb-1">{domain.name}</h3>
                  <div className="flex items-center gap-3">
                    <div className={`text-2xl font-bold ${getScoreColor(domain.currentScore)}`}>
                      {domain.currentScore}
                    </div>
                    <div className="flex items-center gap-1 text-sm">
                      {getTrendIcon(domain.trend)}
                      <span className={domain.trend === 'up' ? 'text-green-400' : domain.trend === 'down' ? 'text-red-400' : 'text-yellow-400'}>
                        {domain.trendValue}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Expand Arrow */}
                <div className="text-white/50">
                  {expandedDomain === domain.id ? '▲' : '▼'}
                </div>
              </button>

              {/* Expanded Content */}
              {expandedDomain === domain.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="border-t border-white/10"
                >
                  {domain.id === 1 ? (
                    // HEALTH DOMAIN - 9 COMPREHENSIVE SECTIONS
                    <div className="p-4 space-y-3">
                      {/* Quick Summary */}
                      <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-xl p-3 border border-emerald-500/20">
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-4 h-4 text-emerald-400" />
                          <span className="text-emerald-400 font-semibold text-sm">Today's Focus</span>
                        </div>
                        <p className="text-white/80 text-sm">{domain.todayFocus}</p>
                      </div>

                      {/* 1. GENERAL CONSTITUTION */}
                      <button
                        onClick={() => setExpandedHealthSection(expandedHealthSection === 'constitution' ? null : 'constitution')}
                        className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center justify-between transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <Activity className="w-5 h-5 text-emerald-400" />
                          <span className="text-white font-medium">General Health Constitution</span>
                        </div>
                        <span className="text-emerald-400 text-sm">
                          {healthData.generalConstitution.physical.score}/100
                        </span>
                      </button>
                      {expandedHealthSection === 'constitution' && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white/5 rounded-lg p-3 space-y-2">
                          <div className="text-sm">
                            <div className="text-white/50 mb-1">Physical:</div>
                            <div className="text-white/80">{healthData.generalConstitution.physical.description}</div>
                          </div>
                          <div className="flex gap-2">
                            {healthData.generalConstitution.physical.strengths.map((s, i) => (
                              <span key={i} className="text-xs bg-green-500/20 text-green-300 px-2 py-1 rounded">{s}</span>
                            ))}
                          </div>
                        </motion.div>
                      )}

                      {/* 2. ORGAN HEALTH */}
                      <button
                        onClick={() => setExpandedHealthSection(expandedHealthSection === 'organs' ? null : 'organs')}
                        className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center justify-between transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <Activity className="w-5 h-5 text-blue-400" />
                          <span className="text-white font-medium">10 Organ-Specific Predictions</span>
                        </div>
                        <span className="text-blue-400 text-xs">Interactive</span>
                      </button>
                      {expandedHealthSection === 'organs' && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white/5 rounded-lg p-3">
                          <div className="grid grid-cols-2 gap-2 mb-3">
                            {Object.entries(healthData.organHealth).slice(0, 6).map(([organ, data]) => (
                              <button
                                key={organ}
                                onClick={() => setSelectedOrgan(selectedOrgan === organ ? null : organ)}
                                className={`p-2 rounded text-xs transition-all ${
                                  selectedOrgan === organ
                                    ? 'bg-emerald-500/20 border border-emerald-500'
                                    : 'bg-white/10'
                                }`}
                              >
                                <div className="text-white capitalize">{organ}</div>
                                <div className={`text-xs ${
                                  data.sensitivity === 'High' ? 'text-red-400' :
                                  data.sensitivity === 'Medium' ? 'text-yellow-400' : 'text-green-400'
                                }`}>{data.sensitivity}</div>
                              </button>
                            ))}
                          </div>
                          {selectedOrgan && (
                            <div className="bg-white/10 rounded p-2 text-xs space-y-1">
                              <div className="text-white/80">{healthData.organHealth[selectedOrgan].current}</div>
                              <div className="text-emerald-400">Remedies: {healthData.organHealth[selectedOrgan].remedies[0]}</div>
                            </div>
                          )}
                        </motion.div>
                      )}

                      {/* 3. DISEASE TIMING */}
                      <button
                        onClick={() => setExpandedHealthSection(expandedHealthSection === 'disease' ? null : 'disease')}
                        className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center justify-between transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-red-400" />
                          <span className="text-white font-medium">Disease Timing & Alerts</span>
                        </div>
                        <span className="text-red-400 text-xs">{healthData.diseaseTimeline.future.length} Future Risks</span>
                      </button>
                      {expandedHealthSection === 'disease' && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-red-500/10 rounded-lg p-3 space-y-2">
                          {healthData.diseaseTimeline.future.slice(0, 2).map((risk, i) => (
                            <div key={i} className="bg-white/10 rounded p-2 text-xs">
                              <div className="text-red-400 font-medium">{risk.window}</div>
                              <div className="text-white/80">{risk.riskType}</div>
                              <div className="text-white/60">Remedy: {risk.remedies[0]}</div>
                            </div>
                          ))}
                        </motion.div>
                      )}

                      {/* 4. SURGERY */}
                      <button
                        onClick={() => setExpandedHealthSection(expandedHealthSection === 'surgery' ? null : 'surgery')}
                        className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center justify-between transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <Shield className="w-5 h-5 text-purple-400" />
                          <span className="text-white font-medium">Surgery Windows</span>
                        </div>
                        <span className="text-purple-400 text-xs">Best Times</span>
                      </button>
                      {expandedHealthSection === 'surgery' && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white/5 rounded-lg p-3 space-y-2 text-xs">
                          <div className="bg-green-500/20 rounded p-2">
                            <div className="text-green-400 font-medium">✅ Best Window</div>
                            <div className="text-white/80">April-June 2029</div>
                          </div>
                          <div className="bg-red-500/20 rounded p-2">
                            <div className="text-red-400 font-medium">⚠️ Avoid</div>
                            <div className="text-white/80">September-November 2028</div>
                          </div>
                        </motion.div>
                      )}

                      {/* 5. ACCIDENT/INJURY */}
                      <button
                        onClick={() => setExpandedHealthSection(expandedHealthSection === 'accident' ? null : 'accident')}
                        className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center justify-between transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <Zap className="w-5 h-5 text-orange-400" />
                          <span className="text-white font-medium">Accident Protection</span>
                        </div>
                        <span className="text-orange-400 text-xs">Safety Alerts</span>
                      </button>
                      {expandedHealthSection === 'accident' && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-orange-500/10 rounded-lg p-3 text-xs space-y-2">
                          {healthData.accidentInjury.future.slice(0, 1).map((risk, i) => (
                            <div key={i} className="bg-white/10 rounded p-2">
                              <div className="text-orange-400 font-medium">{risk.window}</div>
                              <div className="text-white/80">{risk.type}</div>
                              <div className="text-white/60">Protection: {risk.prevention[0]}</div>
                            </div>
                          ))}
                        </motion.div>
                      )}

                      {/* 6. RECOVERY */}
                      <button
                        onClick={() => setExpandedHealthSection(expandedHealthSection === 'recovery' ? null : 'recovery')}
                        className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center justify-between transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <Target className="w-5 h-5 text-blue-400" />
                          <span className="text-white font-medium">Recovery Periods</span>
                        </div>
                        <span className="text-blue-400 text-xs">Healing Cycles</span>
                      </button>

                      {/* 7. LONGEVITY */}
                      <button
                        onClick={() => setExpandedHealthSection(expandedHealthSection === 'longevity' ? null : 'longevity')}
                        className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center justify-between transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <Clock className="w-5 h-5 text-purple-400" />
                          <span className="text-white font-medium">Longevity Prediction</span>
                        </div>
                        <span className="text-purple-400 text-xs">{healthData.longevity.lifeExpectancy}</span>
                      </button>
                      {expandedHealthSection === 'longevity' && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-purple-500/10 rounded-lg p-3">
                          <div className="text-center mb-2">
                            <div className="text-2xl font-bold text-purple-400">{healthData.longevity.overallScore}</div>
                            <div className="text-xs text-white/60">Longevity Score</div>
                          </div>
                          <div className="text-xs text-white/80 space-y-1">
                            {healthData.longevity.lifePhases.slice(0, 3).map((phase, i) => (
                              <div key={i} className="flex justify-between">
                                <span className="text-white/60">{phase.age}:</span>
                                <span className="text-purple-400">{phase.phase}</span>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}

                      {/* 8. HEALTH KARMA */}
                      <button
                        onClick={() => setExpandedHealthSection(expandedHealthSection === 'karma' ? null : 'karma')}
                        className="w-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 hover:from-purple-500/30 hover:to-pink-500/30 rounded-lg p-3 flex items-center justify-between transition-all border border-purple-500/30"
                      >
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-5 h-5 text-pink-400" />
                          <span className="text-white font-medium">Health Karma 🎮</span>
                        </div>
                        <span className="text-pink-400 text-xs">Unlock Stories</span>
                      </button>
                      {expandedHealthSection === 'karma' && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-lg p-3 border border-purple-500/20">
                          <div className="text-xs text-white/80 mb-2">{healthData.funSection.healthKarma.pastLife.description}</div>
                          <div className="space-y-1">
                            {healthData.funSection.healthKarma.unlockableStories.slice(0, 2).map((story, i) => (
                              <div key={i} className="bg-white/10 rounded p-2 flex items-center justify-between">
                                <div>
                                  <div className="text-white text-xs font-medium">{story.title}</div>
                                  <div className="text-white/50 text-xs">{story.unlockCondition}</div>
                                </div>
                                <span className="text-yellow-400">🔒</span>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}

                      {/* 9. TURNING POINTS */}
                      <button
                        onClick={() => setExpandedHealthSection(expandedHealthSection === 'turning' ? null : 'turning')}
                        className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center justify-between transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <Star className="w-5 h-5 text-yellow-400" />
                          <span className="text-white font-medium">Health Turning Points</span>
                        </div>
                        <span className="text-yellow-400 text-xs">Life-Changing</span>
                      </button>
                      {expandedHealthSection === 'turning' && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-yellow-500/10 rounded-lg p-3 space-y-2 text-xs">
                          {healthData.turningPoints.majorShifts.slice(0, 2).map((shift, i) => (
                            <div key={i} className="bg-white/10 rounded p-2">
                              <div className="text-yellow-400 font-medium">Age {shift.age} ({shift.year})</div>
                              <div className="text-white/80">{shift.description}</div>
                            </div>
                          ))}
                        </motion.div>
                      )}
                    </div>
                  ) : domain.id === 2 ? (
                    // CAREER DOMAIN - 12 COMPREHENSIVE SECTIONS
                    <div className="p-4 space-y-3">
                      {/* Quick Summary */}
                      <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl p-3 border border-amber-500/20">
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-4 h-4 text-amber-400" />
                          <span className="text-amber-400 font-semibold text-sm">Today's Focus</span>
                        </div>
                        <p className="text-white/80 text-sm">{domain.todayFocus}</p>
                      </div>

                      {/* 1. GENERAL CONSTITUTION */}
                      <button
                        onClick={() => setExpandedCareerSection(expandedCareerSection === 'constitution' ? null : 'constitution')}
                        className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center justify-between transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-5 h-5 text-amber-400" />
                          <span className="text-white font-medium">General Career Constitution</span>
                        </div>
                        <span className="text-amber-400 text-sm">{careerData.generalConstitution.workNature.type}</span>
                      </button>
                      {expandedCareerSection === 'constitution' && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white/5 rounded-lg p-3 space-y-2 text-sm">
                          <div className="text-white/80">{careerData.generalConstitution.workNature.description}</div>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {careerData.generalConstitution.strengths.slice(0, 4).map((s, i) => (
                              <span key={i} className="text-xs bg-green-500/20 text-green-300 px-2 py-1 rounded">{s}</span>
                            ))}
                          </div>
                        </motion.div>
                      )}

                      {/* 2. CAREER PATH */}
                      <button
                        onClick={() => setExpandedCareerSection(expandedCareerSection === 'path' ? null : 'path')}
                        className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center justify-between transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <BarChart className="w-5 h-5 text-blue-400" />
                          <span className="text-white font-medium">Career Path Indicators</span>
                        </div>
                        <span className="text-blue-400 text-xs">{careerData.careerPath.primaryPaths.length} Paths</span>
                      </button>
                      {expandedCareerSection === 'path' && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white/5 rounded-lg p-3 space-y-2">
                          {careerData.careerPath.primaryPaths.map((path, i) => (
                            <div key={i} className="bg-white/10 rounded p-2 text-xs">
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-white font-medium">{path.field}</span>
                                <span className="text-blue-400">{path.suitability}%</span>
                              </div>
                              <div className="text-white/70">{path.roles.slice(0, 2).join(', ')}</div>
                            </div>
                          ))}
                        </motion.div>
                      )}

                      {/* 3. CAREER TIMING */}
                      <button
                        onClick={() => setExpandedCareerSection(expandedCareerSection === 'timing' ? null : 'timing')}
                        className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center justify-between transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <Clock className="w-5 h-5 text-green-400" />
                          <span className="text-white font-medium">Growth Cycles & Timing</span>
                        </div>
                        <span className="text-green-400 text-xs">{careerData.careerTimeline.future.length} Windows</span>
                      </button>
                      {expandedCareerSection === 'timing' && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-green-500/10 rounded-lg p-3 space-y-2">
                          {careerData.careerTimeline.future.slice(0, 2).map((window, i) => (
                            <div key={i} className="bg-white/10 rounded p-2 text-xs">
                              <div className="text-green-400 font-medium">{window.window}</div>
                              <div className="text-white/80">{window.event}</div>
                              <div className="text-white/60 mt-1">Probability: {window.probability}</div>
                            </div>
                          ))}
                        </motion.div>
                      )}

                      {/* 4. JOB STABILITY */}
                      <button
                        onClick={() => setExpandedCareerSection(expandedCareerSection === 'stability' ? null : 'stability')}
                        className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center justify-between transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <Shield className="w-5 h-5 text-cyan-400" />
                          <span className="text-white font-medium">Job Stability & Changes</span>
                        </div>
                        <span className="text-cyan-400 text-xs">Score: {careerData.jobStability.currentStability.score}</span>
                      </button>

                      {/* 5. SALARY GROWTH */}
                      <button
                        onClick={() => setExpandedCareerSection(expandedCareerSection === 'salary' ? null : 'salary')}
                        className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center justify-between transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-5 h-5 text-emerald-400" />
                          <span className="text-white font-medium">Salary & Growth</span>
                        </div>
                        <span className="text-emerald-400 text-xs">+{careerData.salaryGrowth.current.growthRate}</span>
                      </button>
                      {expandedCareerSection === 'salary' && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-emerald-500/10 rounded-lg p-3 text-xs space-y-2">
                          {Object.entries(careerData.salaryGrowth.projections).slice(0, 3).map(([year, data]) => (
                            <div key={year} className="bg-white/10 rounded p-2">
                              <div className="flex justify-between">
                                <span className="text-white">{year.replace('year', '')}</span>
                                <span className="text-emerald-400">{data.expected}</span>
                              </div>
                              <div className="text-white/60">{data.reason}</div>
                            </div>
                          ))}
                        </motion.div>
                      )}

                      {/* 6. ENTREPRENEURSHIP */}
                      <button
                        onClick={() => setExpandedCareerSection(expandedCareerSection === 'entrepreneur' ? null : 'entrepreneur')}
                        className="w-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 hover:from-purple-500/30 hover:to-pink-500/30 rounded-lg p-3 flex items-center justify-between transition-all border border-purple-500/30"
                      >
                        <div className="flex items-center gap-2">
                          <Award className="w-5 h-5 text-purple-400" />
                          <span className="text-white font-medium">Entrepreneurship Potential</span>
                        </div>
                        <span className="text-purple-400 text-xs">{careerData.entrepreneurship.overall.score}/100</span>
                      </button>
                      {expandedCareerSection === 'entrepreneur' && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-purple-500/10 rounded-lg p-3 border border-purple-500/20 text-xs">
                          <div className="text-purple-400 font-medium mb-2">{careerData.entrepreneurship.overall.recommendation}</div>
                          <div className="space-y-1">
                            {careerData.entrepreneurship.businessTypes.highSuitability.map((biz, i) => (
                              <div key={i} className="bg-white/10 rounded p-2 flex justify-between">
                                <span className="text-white">{biz.type}</span>
                                <span className="text-purple-400">{biz.score}%</span>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}

                      {/* 7. WORKPLACE DYNAMICS */}
                      <button
                        onClick={() => setExpandedCareerSection(expandedCareerSection === 'workplace' ? null : 'workplace')}
                        className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center justify-between transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <Users className="w-5 h-5 text-blue-400" />
                          <span className="text-white font-medium">Workplace Dynamics</span>
                        </div>
                        <span className="text-blue-400 text-xs">Relationships</span>
                      </button>

                      {/* 8. FOREIGN CAREER */}
                      <button
                        onClick={() => setExpandedCareerSection(expandedCareerSection === 'foreign' ? null : 'foreign')}
                        className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center justify-between transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <Globe className="w-5 h-5 text-cyan-400" />
                          <span className="text-white font-medium">Foreign Career</span>
                        </div>
                        <span className="text-cyan-400 text-xs">{careerData.foreignCareer.probability.travel}% Travel</span>
                      </button>
                      {expandedCareerSection === 'foreign' && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-cyan-500/10 rounded-lg p-3 text-xs space-y-2">
                          {careerData.foreignCareer.favorableCountries.slice(0, 3).map((country, i) => (
                            <div key={i} className="bg-white/10 rounded p-2 flex justify-between">
                              <span className="text-white">{country.country}</span>
                              <span className="text-cyan-400">{country.suitability}%</span>
                            </div>
                          ))}
                        </motion.div>
                      )}

                      {/* 9. OBSTACLES */}
                      <button
                        onClick={() => setExpandedCareerSection(expandedCareerSection === 'obstacles' ? null : 'obstacles')}
                        className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center justify-between transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-orange-400" />
                          <span className="text-white font-medium">Obstacles & Remedies</span>
                        </div>
                        <span className="text-orange-400 text-xs">{careerData.obstacles.current.length} Current</span>
                      </button>

                      {/* 10. TURNING POINTS */}
                      <button
                        onClick={() => setExpandedCareerSection(expandedCareerSection === 'turning' ? null : 'turning')}
                        className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center justify-between transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <Star className="w-5 h-5 text-yellow-400" />
                          <span className="text-white font-medium">Career Turning Points</span>
                        </div>
                        <span className="text-yellow-400 text-xs">Life-Changing</span>
                      </button>
                      {expandedCareerSection === 'turning' && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-yellow-500/10 rounded-lg p-3 space-y-2 text-xs">
                          {careerData.turningPoints.major.slice(0, 2).map((point, i) => (
                            <div key={i} className="bg-white/10 rounded p-2">
                              <div className="text-yellow-400 font-medium">Age {point.age} ({point.year})</div>
                              <div className="text-white/80">{point.description}</div>
                            </div>
                          ))}
                        </motion.div>
                      )}

                      {/* 11. RETIREMENT */}
                      <button
                        onClick={() => setExpandedCareerSection(expandedCareerSection === 'retirement' ? null : 'retirement')}
                        className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center justify-between transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <Clock className="w-5 h-5 text-purple-400" />
                          <span className="text-white font-medium">Career Longevity</span>
                        </div>
                        <span className="text-purple-400 text-xs">Age {careerData.careerLongevity.retirementAge.financial}</span>
                      </button>

                      {/* 12. FUN SECTION */}
                      <button
                        onClick={() => setExpandedCareerSection(expandedCareerSection === 'fun' ? null : 'fun')}
                        className="w-full bg-gradient-to-r from-pink-500/20 to-purple-500/20 hover:from-pink-500/30 hover:to-purple-500/30 rounded-lg p-3 flex items-center justify-between transition-all border border-pink-500/30"
                      >
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-5 h-5 text-pink-400" />
                          <span className="text-white font-medium">Career Karma 🎮</span>
                        </div>
                        <span className="text-pink-400 text-xs">Guardian: {careerData.funSection.guardianPlanet.planet}</span>
                      </button>
                      {expandedCareerSection === 'fun' && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-gradient-to-br from-pink-500/10 to-purple-500/10 rounded-lg p-3 border border-pink-500/20">
                          <div className="text-xs text-white/80 mb-2">{careerData.funSection.pastLifeKarma.description}</div>
                          <div className="bg-white/10 rounded p-2 mb-2">
                            <div className="text-pink-400 text-xs font-medium mb-1">Daily Productivity</div>
                            <div className="text-2xl font-bold text-pink-400">{careerData.funSection.dailyProductivityScore.today.score}</div>
                            <div className="text-white/60 text-xs">{careerData.funSection.dailyProductivityScore.today.rating}</div>
                          </div>
                          <div className="text-xs text-yellow-400">
                            Guardian Planet: {careerData.funSection.guardianPlanet.planet} ({careerData.funSection.guardianPlanet.strength})
                          </div>
                        </motion.div>
                      )}
                    </div>
                  ) : domain.id === 3 ? (
                    // LOVE & ROMANCE DOMAIN - 6 COMPREHENSIVE SECTIONS
                    <div className="p-4 space-y-3">
                      {/* Quick Summary */}
                      <div className="bg-gradient-to-r from-pink-500/10 to-rose-500/10 rounded-xl p-3 border border-pink-500/20">
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-4 h-4 text-pink-400" />
                          <span className="text-pink-400 font-semibold text-sm">Today's Focus</span>
                        </div>
                        <p className="text-white/80 text-sm">{domain.todayFocus}</p>
                      </div>

                      {/* 1. GENERAL RELATIONSHIP CONSTITUTION */}
                      <button
                        onClick={() => setExpandedLoveSection(expandedLoveSection === 'constitution' ? null : 'constitution')}
                        className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center justify-between transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <Heart className="w-5 h-5 text-pink-400" />
                          <span className="text-white font-medium">Relationship Constitution</span>
                        </div>
                        <span className="text-pink-400 text-sm">{loveData.generalConstitution.emotionalNature.type}</span>
                      </button>
                      {expandedLoveSection === 'constitution' && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white/5 rounded-lg p-3 space-y-2 text-sm">
                          <div className="text-white/80">{loveData.generalConstitution.emotionalNature.description}</div>
                          <div className="mt-2">
                            <div className="text-xs text-green-400 mb-1">💪 Strengths:</div>
                            {loveData.generalConstitution.emotionalNature.strengths.slice(0, 3).map((s, i) => (
                              <div key={i} className="text-xs text-white/70 mb-1">• {s}</div>
                            ))}
                          </div>
                          <div className="mt-2">
                            <div className="text-xs text-yellow-400 mb-1">⚠️ Watch:</div>
                            {loveData.generalConstitution.emotionalNature.vulnerabilities.slice(0, 2).map((v, i) => (
                              <div key={i} className="text-xs text-white/70 mb-1">• {v}</div>
                            ))}
                          </div>
                        </motion.div>
                      )}

                      {/* 2. PAST RELATIONSHIP STORY */}
                      <button
                        onClick={() => setExpandedLoveSection(expandedLoveSection === 'past' ? null : 'past')}
                        className="w-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 hover:from-purple-500/30 hover:to-pink-500/30 rounded-lg p-3 flex items-center justify-between transition-all border border-purple-500/30"
                      >
                        <div className="flex items-center gap-2">
                          <Heart className="w-5 h-5 text-purple-400" />
                          <span className="text-white font-medium">Past Relationship Story 💫</span>
                        </div>
                        <span className="text-purple-400 text-xs">{loveData.pastRelationshipStory.earlyLoveAffairs.length} Events</span>
                      </button>
                      {expandedLoveSection === 'past' && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-lg p-3 border border-purple-500/20 space-y-2">
                          {loveData.pastRelationshipStory.earlyLoveAffairs.map((affair, i) => (
                            <div key={i} className="bg-white/10 rounded p-2 text-xs">
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-purple-400 font-medium">{affair.avatar} Age {affair.age} ({affair.year})</span>
                                <span className="text-white/60">{affair.type}</span>
                              </div>
                              <div className="text-white/80 mb-1">{affair.description}</div>
                              <div className="text-purple-300 text-xs">Lesson: {affair.lesson}</div>
                            </div>
                          ))}
                          <div className="bg-pink-500/20 rounded p-2 mt-2 border border-pink-500/30">
                            <div className="text-pink-400 font-medium text-xs mb-1">💝 Karmic Lessons</div>
                            {loveData.pastRelationshipStory.karmicLessons.slice(0, 3).map((lesson, i) => (
                              <div key={i} className="text-xs text-white/80">• {lesson}</div>
                            ))}
                          </div>
                        </motion.div>
                      )}

                      {/* 3. LOVE TIMING */}
                      <button
                        onClick={() => setExpandedLoveSection(expandedLoveSection === 'timing' ? null : 'timing')}
                        className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center justify-between transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-rose-400" />
                          <span className="text-white font-medium">Love Timing & Phases</span>
                        </div>
                        <span className="text-rose-400 text-xs">{loveData.loveTimeline.future.length} Windows</span>
                      </button>
                      {expandedLoveSection === 'timing' && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-rose-500/10 rounded-lg p-3 space-y-2">
                          {loveData.loveTimeline.future.slice(0, 3).map((window, i) => (
                            <div key={i} className="bg-white/10 rounded p-2 text-xs">
                              <div className="text-rose-400 font-medium">{window.window}</div>
                              <div className="text-white/80">{window.prediction}</div>
                              <div className="text-white/60 mt-1">Probability: {window.probability}</div>
                            </div>
                          ))}
                        </motion.div>
                      )}

                      {/* 4. MARRIAGE TIMING */}
                      <button
                        onClick={() => setExpandedLoveSection(expandedLoveSection === 'marriage' ? null : 'marriage')}
                        className="w-full bg-gradient-to-r from-pink-500/20 to-red-500/20 hover:from-pink-500/30 hover:to-red-500/30 rounded-lg p-3 flex items-center justify-between transition-all border border-pink-500/30"
                      >
                        <div className="flex items-center gap-2">
                          <Heart className="w-5 h-5 text-pink-400" />
                          <span className="text-white font-medium">Marriage Timing 💍</span>
                        </div>
                        <span className="text-pink-400 text-xs">Age {loveData.marriageTiming.perfectAge.ideal}</span>
                      </button>
                      {expandedLoveSection === 'marriage' && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-gradient-to-br from-pink-500/10 to-red-500/10 rounded-lg p-3 border border-pink-500/20 space-y-2">
                          <div className="bg-white/10 rounded p-2 text-center">
                            <div className="text-2xl font-bold text-pink-400 mb-1">{loveData.marriageTiming.strongestYear.year}</div>
                            <div className="text-xs text-white/80">Strongest Marriage Year</div>
                            <div className="text-xs text-pink-300 mt-1">{loveData.marriageTiming.strongestYear.month}</div>
                            <div className="text-xs text-white/60 mt-1">Probability: {loveData.marriageTiming.strongestYear.probability}</div>
                          </div>
                          <div className="bg-white/10 rounded p-2 text-xs">
                            <div className="text-pink-400 font-medium mb-1">💑 Partner Type</div>
                            <div className="text-white/80">{loveData.marriageTiming.partnerType.nature}</div>
                            <div className="text-white/70 mt-1">Age: {loveData.marriageTiming.partnerType.ageRange}</div>
                          </div>
                          <div className="bg-white/10 rounded p-2 text-xs">
                            <div className="text-cyan-400 font-medium mb-1">💕 Love vs Arranged</div>
                            <div className="text-white/80">Love: {loveData.marriageTiming.loveVsArranged.probability.love} | Arranged: {loveData.marriageTiming.loveVsArranged.probability.arranged}</div>
                          </div>
                        </motion.div>
                      )}

                      {/* 5. MARRIED LIFE */}
                      <button
                        onClick={() => setExpandedLoveSection(expandedLoveSection === 'marriedlife' ? null : 'marriedlife')}
                        className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center justify-between transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <Home className="w-5 h-5 text-emerald-400" />
                          <span className="text-white font-medium">Married Life Quality</span>
                        </div>
                        <span className="text-emerald-400 text-xs">Score: {loveData.marriedLife.quality.overallScore}</span>
                      </button>
                      {expandedLoveSection === 'marriedlife' && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-emerald-500/10 rounded-lg p-3 text-xs space-y-2">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-white">Stability:</span>
                            <span className="text-emerald-400">{loveData.marriedLife.quality.stability.score}/100</span>
                          </div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-white">Emotional Connection:</span>
                            <span className="text-pink-400">{loveData.marriedLife.quality.emotionalConnection.score}/100</span>
                          </div>
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-white">Compatibility:</span>
                            <span className="text-cyan-400">{loveData.marriedLife.quality.compatibilityScore}/100</span>
                          </div>
                          <div className="bg-white/10 rounded p-2 mt-2">
                            <div className="text-emerald-400 mb-1">Partner Personality</div>
                            <div className="text-white/80">{loveData.marriedLife.partnerPersonality.nature}</div>
                            <div className="text-white/70 mt-1">Loyalty: {loveData.marriedLife.partnerPersonality.loyaltyLevel.score}/100</div>
                          </div>
                        </motion.div>
                      )}

                      {/* 6. PROBLEMS & REMEDIES */}
                      <button
                        onClick={() => setExpandedLoveSection(expandedLoveSection === 'problems' ? null : 'problems')}
                        className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center justify-between transition-all"
                      >
                        <div className="flex items-center gap-2">
                          <Shield className="w-5 h-5 text-amber-400" />
                          <span className="text-white font-medium">Problems & Remedies</span>
                        </div>
                        <span className="text-amber-400 text-xs">Protection</span>
                      </button>
                      {expandedLoveSection === 'problems' && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-amber-500/10 rounded-lg p-3 space-y-2 text-xs">
                          <div className="bg-white/10 rounded p-2">
                            <div className="text-green-400 mb-1">✅ Low Risks</div>
                            <div className="text-white/80">Divorce: {loveData.marriageProblems.specificPredictions.divorceRisk.probability}</div>
                            <div className="text-white/80">Separation: {loveData.marriageProblems.specificPredictions.separationRisk.probability}</div>
                          </div>
                          <div className="bg-red-500/20 rounded p-2 border border-red-500/30">
                            <div className="text-red-400 mb-1">⚠️ Watch For (Medium)</div>
                            <div className="text-white/80">Mars (Anger): {loveData.marriageProblems.planetaryCauses.mars.severity}</div>
                            <div className="text-white/70 text-xs mt-1">Remedy: {loveData.marriageProblems.planetaryCauses.mars.remedies[0]}</div>
                          </div>
                          <div className="bg-cyan-500/20 rounded p-2 border border-cyan-500/30">
                            <div className="text-cyan-400 mb-1">💝 For Harmony</div>
                            {loveData.marriageProblems.comprehensiveRemedies.forHarmony.daily.slice(0, 2).map((r, i) => (
                              <div key={i} className="text-white/70">• {r}</div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </div>
                  ) : (
                    // OTHER DOMAINS - KEEP ORIGINAL
                    <div className="p-4 space-y-4">
                      <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-xl p-3 border border-cyan-500/20">
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-4 h-4 text-cyan-400" />
                          <span className="text-cyan-400 font-semibold text-sm">Today's Focus</span>
                        </div>
                        <p className="text-white/80 text-sm">{domain.todayFocus}</p>
                      </div>

                      <div className="grid grid-cols-1 gap-3">
                        <div className="bg-white/5 rounded-lg p-3">
                          <div className="text-xs text-white/50 mb-1">This Week</div>
                          <div className="text-white text-sm">{domain.weeklyInsight}</div>
                        </div>
                        <div className="bg-white/5 rounded-lg p-3">
                          <div className="text-xs text-white/50 mb-1">This Month</div>
                          <div className="text-white text-sm">{domain.monthlyPrediction}</div>
                        </div>
                        <div className="bg-white/5 rounded-lg p-3">
                          <div className="text-xs text-white/50 mb-1">This Year</div>
                          <div className="text-white text-sm">{domain.yearlyOutlook}</div>
                        </div>
                      </div>

                      <div>
                        <div className="text-sm font-semibold text-green-400 mb-2">✨ Key Strengths</div>
                        <div className="flex flex-wrap gap-2">
                          {domain.keyStrengths.map((strength, i) => (
                            <span key={i} className="bg-green-500/20 text-green-300 px-3 py-1 rounded-full text-xs border border-green-500/30">
                              {strength}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div>
                        <div className="text-sm font-semibold text-yellow-400 mb-2">⚠️ Areas to Watch</div>
                        <div className="flex flex-wrap gap-2">
                          {domain.areasToWatch.map((area, i) => (
                            <span key={i} className="bg-yellow-500/20 text-yellow-300 px-3 py-1 rounded-full text-xs border border-yellow-500/30">
                              {area}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div>
                        <div className="text-sm font-semibold text-cyan-400 mb-2">💡 Recommendations</div>
                        <div className="space-y-2">
                          {domain.recommendations.map((rec, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <span className="text-cyan-400 mt-0.5">•</span>
                              <span className="text-white/70 text-sm">{rec}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Overall Summary Card */}
        <div className="fixed bottom-20 left-4 right-4 bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-lg rounded-2xl p-4 border border-purple-500/30">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-white font-semibold mb-1">Your Cosmic Balance</div>
              <div className="text-xs text-white/60">All domains harmonized</div>
            </div>
            <div className="text-4xl">✨</div>
          </div>
        </div>
      </div>
    </CosmicBackground>
  );
};

export default DashboardTrillionDollar;
